import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'

const CONCURRENT_UPLOADS = 12 // Upload 12 files in parallel for optimal performance

export default class extends Controller {
  static targets = ['input', 'progress', 'progressBar', 'progressText', 'error', 'submit', 'fileCount']
  static values = { url: String }

  connect() {
    console.log('FolderUploadController connected')

    // Register change event listener for folder input
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener('change', this.handleFolderSelection.bind(this))
    }
  }

  handleFolderSelection(event) {
    const files = Array.from(event.target.files)
    if (!files || files.length === 0) return

    console.log(`Folder selected with ${files.length} files`)

    // Show file count
    if (this.hasFileCountTarget) {
      this.fileCountTarget.textContent = `${files.length} arquivos encontrados`
      this.fileCountTarget.classList.remove('hidden')
    }

    // Validate folder structure (should contain @c.us folders)
    const hasConversationFolders = files.some(file =>
      file.webkitRelativePath.includes('@c.us')
    )

    if (!hasConversationFolders) {
      this.handleError('A pasta selecionada não parece conter conversas válidas (nenhuma pasta @c.us encontrada)')
      return
    }

    // Generate unique S3 prefix
    this.s3Prefix = `imports/${this.generateUUID()}/`
    console.log(`Generated S3 prefix: ${this.s3Prefix}`)

    // Show progress UI
    this.showProgress()

    // Disable submit button during upload
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
    }

    // Disable folder input to prevent multiple uploads
    this.inputTarget.disabled = true

    // Start uploading all files
    this.uploadAllFiles(files)
  }

  async uploadAllFiles(files) {
    this.totalFiles = files.length
    this.uploadedFiles = 0
    this.failedFiles = 0
    this.uploadsInProgress = 0

    // Create queue of files to upload
    this.fileQueue = [...files]

    // Start initial batch of concurrent uploads
    const initialBatch = Math.min(CONCURRENT_UPLOADS, this.fileQueue.length)
    for (let i = 0; i < initialBatch; i++) {
      this.uploadNextFile()
    }
  }

  async uploadNextFile() {
    // Check if queue is empty
    if (this.fileQueue.length === 0) {
      // Check if all uploads are complete
      if (this.uploadsInProgress === 0) {
        this.handleAllUploadsComplete()
      }
      return
    }

    const file = this.fileQueue.shift()
    this.uploadsInProgress++

    try {
      await this.uploadSingleFile(file)
      this.uploadedFiles++
    } catch (error) {
      console.error(`Failed to upload ${file.name}:`, error)
      this.failedFiles++
    } finally {
      this.uploadsInProgress--
      this.updateProgress()

      // Upload next file in queue
      this.uploadNextFile()
    }
  }

  uploadSingleFile(file) {
    return new Promise((resolve, reject) => {
      // Build S3 key preserving folder structure
      const relativePath = file.webkitRelativePath
      const s3Key = `${this.s3Prefix}${relativePath}`

      console.log(`Uploading: ${relativePath} -> ${s3Key}`)

      // Create a new File object with the S3 key as the name
      // This ensures the file is uploaded to the correct S3 path
      const fileWithS3Path = new File([file], s3Key, { type: file.type })

      const upload = new DirectUpload(fileWithS3Path, this.urlValue, {
        directUploadWillStoreFileWithXHR: (request) => {
          // Add custom header with original relative path
          request.setRequestHeader('X-File-Path', relativePath)
        }
      })

      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob)
        }
      })
    })
  }

  updateProgress() {
    const percentage = (this.uploadedFiles / this.totalFiles) * 100

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
    }

    if (this.hasProgressTextTarget) {
      const statusText = this.failedFiles > 0
        ? `${this.uploadedFiles}/${this.totalFiles} arquivos (${this.failedFiles} falhas)`
        : `${this.uploadedFiles}/${this.totalFiles} arquivos`

      this.progressTextTarget.textContent = `${Math.round(percentage)}% - ${statusText}`
    }

    console.log(`Progress: ${this.uploadedFiles}/${this.totalFiles} (${this.failedFiles} failed)`)
  }

  handleAllUploadsComplete() {
    console.log(`All uploads complete! ${this.uploadedFiles} succeeded, ${this.failedFiles} failed`)

    if (this.failedFiles > 0) {
      this.handleError(`Upload concluído com ${this.failedFiles} falha(s). ${this.uploadedFiles} arquivos foram enviados com sucesso.`)
      return
    }

    // Update progress to 100%
    this.updateProgress()

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = '✓ Upload concluído! Todos os arquivos foram enviados.'
      this.progressTextTarget.classList.remove('text-blue-600')
      this.progressTextTarget.classList.add('text-green-600', 'font-semibold')
    }

    // Add hidden fields for form submission
    this.addHiddenField('conversation_backup_import[s3_prefix]', this.s3Prefix)
    this.addHiddenField('conversation_backup_import[file_count]', this.totalFiles)

    // Enable submit button
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      this.submitTarget.classList.add('animate-pulse')
    }
  }

  addHiddenField(name, value) {
    // Check if field already exists
    let field = this.element.querySelector(`input[name="${name}"]`)

    if (field) {
      field.value = value
    } else {
      field = document.createElement('input')
      field.type = 'hidden'
      field.name = name
      field.value = value
      this.element.appendChild(field)
    }

    console.log(`Added hidden field: ${name} = ${value}`)
  }

  showProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove('hidden')
    }
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
    if (this.hasFileCountTarget) {
      this.fileCountTarget.classList.add('hidden')
    }
  }

  handleError(error) {
    console.error('Folder upload error:', error)

    // Hide progress, show error
    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('hidden')
    }

    if (this.hasErrorTarget) {
      this.errorTarget.textContent = typeof error === 'string' ? error : `Erro no upload: ${error}. Por favor, tente novamente.`
      this.errorTarget.classList.remove('hidden')
    }

    // Re-enable input for retry
    this.inputTarget.disabled = false
    this.inputTarget.value = ''

    // Keep submit button disabled after error
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
    }
  }

  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0
      const v = c === 'x' ? r : (r & 0x3 | 0x8)
      return v.toString(16)
    })
  }

  disconnect() {
    // Cleanup if needed
  }
}
